import { QUERY_DEFAULTS } from '@/shared/api/openmetadata';
import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { DEFAULT_QUERY_OPTIONS, useInfiniteQueryWithBoundary } from '@/shared/lib/query';
import { glossaryKeys } from '@/shared/lib/query/queryKeys';
import type { SortOption } from '@/shared/types/common';

import type { FilterState } from '@/entities/discovery/domain/types';

import { searchTaggedAssets } from '@/features/glossary/api/glossaryTermAssetsApi';

type UseTaggedAssetsParams = {
  termFqn: string;
  filters: FilterState;
  query?: string;
  enabled?: boolean;
  sortBy: SortOption['field'];
  sortOrder: SortOption['direction'];
};

export const useTaggedAssets = ({
  termFqn,
  filters,
  query = '',
  enabled = true,
  sortBy = 'updatedAt',
  sortOrder = 'desc',
}: UseTaggedAssetsParams) => {
  return useInfiniteQueryWithBoundary({
    queryKey: glossaryKeys.taggedAssets(termFqn, filters, query, sortBy, sortOrder),
    queryFn: ({ pageParam = 0 }) =>
      searchTaggedAssets(
        termFqn,
        filters,
        query,
        pageParam as number,
        QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
        sortBy,
        sortOrder
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage: OpenMetadataResponse, allPages: OpenMetadataResponse[]) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.hits.hits.length, 0);
      const total = lastPage.hits.total.value;
      return totalFetched < total ? totalFetched : undefined;
    },
    enabled: enabled && !!termFqn,
    staleTime: 5 * 60 * 1000,
    ...DEFAULT_QUERY_OPTIONS,
  });
};
